import { FC } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Field, Form, Formik } from "formik";
import { AutocompleteField, CheckboxField } from "components/UI/Form";
import { QueryClient, useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { bulkDeleteCaseItems, bulkUpdateCaseItems } from "src/pages/UserDashboard/Cases/api/casesApi.ts";
import {
  deniedReasonOptions,
  DeniedReasonOption,
  CaseStatus,
  bulkSettableCaseStatuses,
} from "src/pages/UserDashboard/Cases/api/constants.ts";
import { CaseType } from "src/pages/UserDashboard/WorkPackets/Cases/case-types.ts";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useFeatureFlag } from "src/feature-flags";

interface BulkActionPopupProps {
  selectedItems: string[];
  excludedItems: string[];
  allSelected: boolean;
  caseId: string;
  caseType: CaseType;
  onClose: () => void;
  open: boolean;
  deselectRows: () => void;
}

interface BulkUpdateParams {
  selectedItems: string[];
  excludedItems: string[];
  allSelected: boolean;
  caseId: string;
  caseType: CaseType;
  updateParams: Record<string, any>;
  queryClient: QueryClient;
}

const updateCaseItemsMutationOptions: UseMutationOptions<void, Error, BulkUpdateParams> = {
  mutationFn: async params => {
    if (params.updateParams.DELETE) {
      await bulkDeleteCaseItems(params);
    } else {
      await bulkUpdateCaseItems(params);
    }
  },
  mutationKey: ["update-case-items"],

  onSuccess: async (_data, { caseType, caseId, queryClient }) => {
    queryClient.invalidateQueries({ queryKey: ["cases", caseType, caseId, "items"] });
  },
};

interface UpdateParams {
  STATUS: string;
  DENIED_REASON?: string;
  DELETE: boolean;
}

const validationSchema = Yup.object<UpdateParams>().shape({
  STATUS: Yup.string().when("DELETE", {
    is: false,
    then: schema => schema.required("Case Item status is required"),
    otherwise: schema => schema.notRequired(),
  }),
  DELETE: Yup.boolean(),
});

export const BulkActionPopup: FC<BulkActionPopupProps> = ({
  selectedItems,
  excludedItems,
  caseId,
  caseType,
  allSelected,
  onClose,
  open,
  deselectRows,
}) => {
  const queryClient = useQueryClient();
  const initialValues: UpdateParams = {
    STATUS: "",
    DENIED_REASON: undefined,
    DELETE: false,
  };
  const { mutateAsync, isPending } = useMutation(updateCaseItemsMutationOptions);
  const onSubmit = async (updateParams: UpdateParams) => {
    const data: BulkUpdateParams = {
      caseId,
      excludedItems: allSelected ? excludedItems || [] : [],
      selectedItems: allSelected ? [] : selectedItems,
      allSelected,
      caseType,
      updateParams,
      queryClient,
    };
    await mutateAsync(data);
    onClose();
    deselectRows();
    toast.success("Case items updated successfully");
  };

  const { value: showDeniedReasonField } = useFeatureFlag("WORK_PACKET_DENIAL_REASONS", false);

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Actions</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        {({ values, dirty, isValid }) => (
          <Form>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2}>
                {caseType != CaseType.SHORTAGE_SETTLEMENT && (
                  <Box
                    display="grid"
                    gridTemplateColumns="1fr 1fr"
                    gap={4}
                    {...(values.DELETE && { inert: "", style: { opacity: 0.5 } })}
                  >
                    <Field
                      name={"STATUS"}
                      label="Case Item Status"
                      component={AutocompleteField}
                      placeholder="Select a Status"
                      getOptionLabel={(option: any) => option.title}
                      options={bulkSettableCaseStatuses[caseType]}
                    />
                    {showDeniedReasonField && (
                      <Field
                        name={"DENIED_REASON"}
                        label="Denied Reason"
                        component={AutocompleteField}
                        placeholder="Denied Reason"
                        getOptionLabel={(option: DeniedReasonOption) => option.title}
                        options={deniedReasonOptions}
                        disabled={values.STATUS !== CaseStatus.Denied}
                      />
                    )}
                  </Box>
                )}
                <Field type="checkbox" name="DELETE" component={CheckboxField} label="Delete selected work packets" />
              </Box>
            </DialogContent>
            <DialogActions style={{ background: "transparent" }}>
              <Button variant="outlined" onClick={() => onClose()} disabled={isPending}>
                Cancel {isValid}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!dirty || !isValid || isPending}
                sx={{ paddingX: "24px" }}
              >
                <span>Save Changes</span>
                {isPending && <CircularProgress size={14} style={{ marginLeft: "12px", color: "#101828" }} />}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
